/**
 * Admin › Activity — who did what, and when.
 *
 * ADMIN ONLY, and the gate is `admin` rather than `commissioner`: commissioners can move FAAB,
 * affirm a trade on somebody's behalf and edit a keeper slate, so they are the people this log
 * most often needs to be evidence ABOUT. That is enforced in `firestore.rules`, not here — the
 * page being unreachable is a courtesy; the database refusing the read is the control.
 *
 * MOST OF WHAT IS ON THIS PAGE IS NOT STORED ANYWHERE. It is derived from the records themselves
 * (`domain/auditLog.ts`) — every keeper slate knows who saved it, every FAAB line knows who
 * recorded it — which means the log covers 2025 as well as today, and cannot be falsified by
 * writing a row. The stored half is the four things derivation cannot see: sign-ins, page views,
 * deletions and access decisions. Each row says which it is.
 *
 * IT IS LOUD ABOUT THE TWO THINGS AN AUDIT LOG CAN QUIETLY LIE ABOUT: how far back it goes, and
 * what it does not cover. Both are printed at the top, because a log that silently starts on
 * Tuesday reads as a league that did nothing before Tuesday.
 */

import { Fragment, useEffect, useMemo, useState } from 'react';
import {
  KIND_LABEL, VIA_NOTE, actingSpans, actorsIn, attributeNotes, attributeVia, countByKind,
  deriveEvents, eventsByDay, filterEvents, fromEntry, mergeEvents, toCsv, viaNotes,
  type AuditEvent, type EventKind,
} from '../domain/auditLog.js';
import { GMS } from '../store/session.js';
import { note } from './auditor.js';
import { LEAGUE_ZONE, ZONES, bothZones, clock24In, dayIn } from '../domain/deadlines.js';
import { managerShort } from '../domain/identity.js';
import { GmBadge } from './GmBadge.js';
import { atLeast, type LeagueStore, type Role } from '../store/types.js';
import { dock } from './dock.js';

const SEASON = 2026;
/** The first render, per the long-list pattern. */
/** Which of the league's two zones the log is read in. Per browser, not per league. */
const ZONE_KEY = 'ffl.logtz.v1';

const LOG_CAP = 200;
/** Chips shown on a phone before the `+N` that wraps the rest open. */
const PHONE_CHIPS = 4;

/** The order the chips appear in: what people do most, first. */
const KINDS: EventKind[] = [
  'keeper', 'faab', 'trade', 'proposal', 'vote', 'pick', 'farm',
  'interpretation', 'override', 'settings', 'draft', 'roster', 'access', 'avatar',
  'sign-in', 'acting-as', 'view', 'delete',
];

export function AdminAudit(
  { gm, role, store }: { gm: string; role: Role; store: LeagueStore },
) {
  /*
   * The page refuses itself as well as being unreachable from the nav. A commissioner who lands
   * here from a bookmark gets a sentence rather than an empty table, because an empty table reads
   * as "nothing has happened".
   */
  if (!atLeast(role, 'admin')) {
    return (
      <div className="app admin activity">
        <section className="card actstate">
          <h3>This log is admin-only</h3>
          <p className="fact">
            Commissioners are who it is most often evidence about, so the database refuses the read.
          </p>
        </section>
      </div>
    );
  }
  return <Log gm={gm} store={store} />;
}

function Log({ gm, store }: { gm: string; store: LeagueStore }) {
  const [events, setEvents] = useState<AuditEvent[] | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  /** Bumped after a note is written, to re-read the log. */
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let alive = true;
    /*
     * ONE LOAD, ON DEMAND, and everything in parallel. This is the only screen that reads every
     * collection at once, which is exactly why it must not be a live subscription: nine other
     * managers would be holding listeners on documents they are not allowed.
     *
     * Each read falls back to empty rather than failing the page. A store that predates a
     * collection — the offline build, an old backup — should show the log it can build, not an
     * error about the one thing it is missing.
     */
    const safe = <T,>(p: Promise<T> | undefined, fallback: T): Promise<T> =>
      (p ?? Promise.resolve(fallback)).catch(() => fallback);

    void (async () => {
      try {
        const [
          declarations, preferences, trades, liveTrades, faab, proposals, interpretations,
          overrides, picks, farm, settings, draftState, managers, pairings, avatars, recorded,
        ] = await Promise.all([
          safe(store.declarations(SEASON), []),
          safe(store.preferences(SEASON), []),
          safe(store.tradesAll(), []),
          safe(store.liveTrades?.(SEASON), []),
          safe(store.faab(SEASON), []),
          safe(store.proposals(SEASON), []),
          safe(store.interpretations(SEASON), []),
          safe(store.overrides(SEASON), []),
          safe(store.picks(SEASON), []),
          safe(store.farmNominations(SEASON), []),
          safe(store.settings(SEASON), null),
          safe(store.draftState(SEASON), null),
          safe(store.managers(), []),
          safe(store.teamPairings(), []),
          safe(store.avatars(), []),
          safe(store.audit?.(SEASON), []),
        ]);
        if (!alive) return;
        const derived = deriveEvents({
          declarations, preferences, trades, liveTrades, faab, proposals, interpretations,
          overrides, picks, farm, settings, draftState, managers, pairings, avatars,
        });
        /*
         * The spans go on LAST, over the merged timeline: a recorded acting-as row explains
         * derived events that name somebody else, so it cannot be applied before both halves are
         * in one list and sorted.
         */
        /*
         * Spans first, then an admin's notes ON TOP — a person who says "that was me" is
         * correcting the inference, so his answer has to be applied last or the guess would
         * overwrite the correction.
         */
        setEvents(attributeNotes(
          attributeVia(mergeEvents(derived, recorded.map(fromEntry)), actingSpans(recorded)),
          viaNotes(recorded),
        ));
      } catch (e) {
        if (alive) setFailed(e instanceof Error ? e.message : 'could not be read');
      }
    })();
    return () => { alive = false; };
  }, [store, reload]);

  /**
   * Say who was really at the keyboard, for a row no span can explain.
   *
   * Written as an ordinary append-only row whose target names the event it is about, so the claim
   * is itself in the log with a name and a time on it. Nothing is overwritten — a later note wins
   * on read, and both survive.
   */
  const saveVia = (e: AuditEvent, via: string, why: string) => {
    note(store, gm, {
      kind: 'note',
      what: via
        ? `Noted that ${via} was acting as ${e.actor} — ${e.what}`
        : `Cleared the attribution on — ${e.what}`,
      target: `${VIA_NOTE}:${e.id}`,
      detail: { Via: via || undefined, Why: why.trim() || undefined, Event: e.id },
    });
    // The write is fire-and-forget by design, so give it a moment before re-reading.
    setTimeout(() => setReload((n) => n + 1), 400);
  };

  /** The row whose sheet is open. One at a time; Escape and the scrim both close it. */
  const [open, setOpen] = useState<AuditEvent | null>(null);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(null); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  /*
   * WHOSE CLOCK THE LOG IS IN (Tim, 8 September: "logged actions should be posted in toggleable
   * MT/ET times").
   *
   * The rows read their time off the ISO string, which is UTC — so a pick entered at 8:07pm on
   * draft night was logged as `02:07` and filed under the following day's heading. The league
   * runs in two zones and always has; the page picks one and says which, and the choice sticks.
   *
   * It opens on the reader's own zone where that is one of the two, and on the league zone
   * otherwise, because the commonest reason to open this page is "what happened just now" and the
   * answer should be in the clock he has just looked at.
   */
  const [zone, setZone] = useState<string>(() => {
    try {
      const saved = localStorage.getItem(ZONE_KEY);
      if (saved && ZONES.some((z) => z.tz === saved)) return saved;
      const here = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (ZONES.some((z) => z.tz === here)) return here;
    } catch { /* a browser that refuses storage still gets a working page */ }
    return LEAGUE_ZONE;
  });
  const showZone = (tz: string) => {
    setZone(tz);
    try { localStorage.setItem(ZONE_KEY, tz); } catch { /* as above */ }
  };

  const [kind, setKind] = useState<EventKind | 'all'>('all');
  const [actor, setActor] = useState<string>('all');
  const [q, setQ] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const all = events ?? [];
  const shown = useMemo(
    () => filterEvents(all, { kind, actor, q, ...(from ? { from } : {}), ...(to ? { to } : {}) }),
    [all, kind, actor, q, from, to],
  );
  const counts = useMemo(() => countByKind(all), [all]);
  const actors = useMemo(() => actorsIn(all), [all]);
  const oldest = all.length ? all[all.length - 1]!.at.slice(0, 10) : null;
  const recordedFrom = useMemo(() => {
    const rec = all.filter((e) => e.source === 'recorded');
    return rec.length ? rec[rec.length - 1]!.at.slice(0, 10) : null;
  }, [all]);

  const download = () => {
    const blob = new Blob([toCsv(shown)], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ffl-activity-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  /*
   * THE LONG-LIST PATTERN (Pass 16, call 2): count, filters, the first 200 rows, a centred foot
   * that says how many there are and shows the rest on request. Grouped by day because time is
   * this page's axis.
   */
  const [showAll, setShowAll] = useState(false);
  const capped = showAll ? shown : shown.slice(0, LOG_CAP);
  const days = useMemo(() => eventsByDay(capped, zone), [capped, zone]);
  const filtered = kind !== 'all' || actor !== 'all' || q !== '' || from !== '' || to !== '';
  const clearFilters = () => { setKind('all'); setActor('all'); setQ(''); setFrom(''); setTo(''); };
  /* The phone shows four chips and a `+N` that wraps the row open. */
  const [chipsOpen, setChipsOpen] = useState(false);
  const kinds = KINDS.filter((k) => counts.get(k));

  const scope = (iso: string | null) => (iso
    ? new Date(`${iso}T12:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : null);

  return (
    <div className="app admin activity">
      {/*
        WHAT THIS LOG DOES AND DOES NOT COVER, said before anything is read rather than in a
        footnote. The commonest way to misread an audit log is to take its earliest row for the
        earliest event. The dates are computed from the oldest rows, never typed.
      */}
      <p className="actscope">
        Derived rows since <b>{scope(oldest) ?? 'the first record'}</b> · logged rows since{' '}
        <b>{scope(recordedFrom) ?? 'the day logging shipped'}</b> · pages record opens, never contents
      </p>

      {events !== null && (
        <div className={`actkinds ${chipsOpen ? 'open' : ''}`}>
          {kinds.map((k, i) => (
            <button type="button" key={k} className={`kindchip ${k} ${kind === k ? 'on' : ''} ${i >= PHONE_CHIPS ? 'later' : ''}`}
              onClick={() => setKind(kind === k ? 'all' : k)}>
              {KIND_LABEL[k]} <span className="n">{counts.get(k)}</span>
            </button>
          ))}
          {kinds.length > PHONE_CHIPS && !chipsOpen && (
            <button type="button" className="kindchip more" onClick={() => setChipsOpen(true)}>
              +{kinds.length - PHONE_CHIPS} <span className="n">▾</span>
            </button>
          )}
        </div>
      )}

      <div className="acttools">
        <input className="actsearch" type="search" placeholder="Search who, what, or target"
          aria-label="Search the log" value={q} onChange={(e) => setQ(e.target.value)} />
        <select value={actor} onChange={(e) => setActor(e.target.value)} aria-label="Filter by who">
          <option value="all">Who: anyone</option>
          {actors.map((a) => <option key={a} value={a}>{managerShort(a)}</option>)}
        </select>
        <span className="actrange">
          <label><span className="peyebrow">from</span>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
          <label><span className="peyebrow">to</span>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></label>
        </span>
        <span className="spacer" />
        {/*
          Two zones, named rather than offered as a list: the league has exactly these two and a
          select would imply otherwise. The pressed one is the clock every row below is in.
        */}
        <span className="panes small acttz" role="group" aria-label="Time zone">
          {ZONES.map((z) => (
            <button type="button" key={z.tz} className={`pane ${zone === z.tz ? 'on' : ''}`}
              aria-pressed={zone === z.tz} onClick={() => showZone(z.tz)}>{z.label}</button>
          ))}
        </span>
        <span className="actcount">
          {events === null ? '' : shown.length === all.length ? `all ${all.length.toLocaleString()}` : `${shown.length.toLocaleString()} of ${all.length.toLocaleString()}`}
        </span>
        <button type="button" className="linkbtn" onClick={download} disabled={!shown.length}>
          Export CSV
        </button>
      </div>

      {failed && (
        <section className="card actstate">
          <h3>The log could not be read</h3>
          <p className="fact">{failed}</p>
        </section>
      )}
      {events === null && !failed && <p className="card actstate fact">Reading the log…</p>}

      {events !== null && days.length === 0 && (
        <section className="card actstate">
          <h3>Nothing matches</h3>
          <p className="fact">
            0 of {all.length.toLocaleString()} — the filter is narrow, not the league idle.
            {filtered && <> <button type="button" className="linkbtn" onClick={clearFilters}>Clear filters</button></>}
          </p>
        </section>
      )}

      {events !== null && days.length > 0 && (
        <section className="card actlog">
          {days.map((d) => (
            <Fragment key={d.day}>
              <div className="actday">{dayLabel(d.day, zone)}</div>
              <ol className="actlist">
                {d.events.map((e) => (
                  <Row key={e.id} e={e} me={gm} tz={zone} onOpen={() => setOpen(e)} />
                ))}
              </ol>
            </Fragment>
          ))}
          <div className="actfoot">
            {shown.length > LOG_CAP && !showAll
              ? <>First {LOG_CAP} of {shown.length.toLocaleString()} · <button type="button" className="linkbtn" onClick={() => setShowAll(true)}>Show all</button></>
              : <>{shown.length.toLocaleString()} row{shown.length === 1 ? '' : 's'}</>}
          </div>
        </section>
      )}

      {open && (
        <Sheet
          e={open}
          onClose={() => setOpen(null)}
          onVia={(via, why) => { saveVia(open, via, why); setOpen(null); }} />
      )}
    </div>
  );
}

/**
 * Everything about one row.
 *
 * The list is a scannable summary and the question that always follows it is "what exactly
 * changed" — which, before this, was answered by going and opening the record. The sheet carries
 * the whole event: the timestamp in both league zones, who, whose, whether somebody else was at
 * the keyboard, and every field the record had.
 */
function Sheet(
  { e, onClose, onVia }:
  { e: AuditEvent; onClose: () => void; onVia: (via: string, why: string) => void },
) {
  const [noting, setNoting] = useState(false);
  const [via, setVia] = useState(e.via ?? '');
  const [why, setWhy] = useState(e.viaWhy ?? '');
  /* Docked — `position: fixed` inside `main`, which is transformed. See `src/ui/dock.tsx`. */
  return dock(
    <>
      <button type="button" className="rulescrim" aria-label="Close" onClick={onClose} />
      <aside className="rulepanel auditsheet" role="dialog" aria-modal="true" aria-label={e.what}>
        <header>
          <div>
            <span className="ruleid">{KIND_LABEL[e.kind]} · {e.source === 'derived' ? 'record' : 'logged'}</span>
            <h2>{e.what}</h2>
          </div>
          <button type="button" className="panelclose" aria-label="Close" onClick={onClose}>×</button>
        </header>
        <div className="rulebody">
          <dl className="auditfacts">
            {/*
              BOTH ZONES, like every other time in this app. The league is not in one, and "20:33"
              on an audit row is the question "whose 20:33" waiting to be asked.
            */}
            <Fact label="When" value={bothZones(e.at)} />
            <Fact label="Who" value={e.via ? `${managerShort(e.actor)} (the record’s name)` : managerShort(e.actor)} />
            {e.via && (
              <Fact
                label="Really who"
                tone={e.viaInferred ? 'amber' : undefined}
                value={`${managerShort(e.via)} — ${e.viaInferred ? 'inferred from an acting-as span' : e.viaNotedAt ? 'noted by hand' : 'recorded'}`} />
            )}
            {e.viaWhy && <Fact label="Why" value={e.viaWhy} />}
            {e.onBehalfOf && <Fact label="On behalf of" value={managerShort(e.onBehalfOf)} />}
            {e.target && <Fact label="Record" value={e.target} mono />}
            <Fact
              label="Source"
              value={e.source === 'derived'
                ? 'derived from the record'
                : 'logged by a browser at the time'} />
            {e.detail?.map((x) => <Fact key={x.label} label={x.label} value={x.value} />)}
          </dl>

          {e.detail?.length ? null : (
            <p className="note">
              {/*
                Said rather than left blank. A sheet with nothing under it reads as a record with
                nothing in it, and these two kinds genuinely have nothing more to give.
              */}
              This one has nothing beyond the line above — a view and a sign-in are the whole event.
            </p>
          )}

          {e.viaInferred && (
            <p className="impact">
              Inferred, not recorded — an acting-as span covered this moment. The app is saying{' '}
              {managerShort(e.via!)} may have been at the keyboard, not that he was.
            </p>
          )}

          {e.viaNotedAt && (
            <p className="fact">
              Noted by hand on {e.viaNotedAt.slice(0, 10)}: somebody’s account, itself a row in the
              log.
            </p>
          )}

          {/*
            THE ROWS THE SPANS CANNOT REACH.
            Acting-as is only logged from the day it shipped, so everything before it reads as the
            impersonated manager's own act for ever — the 18 August farm nomination is the case.
            An admin can say what happened, and his saying so is a third kind of provenance: not
            read off the record, not inferred, but claimed, with his name and the time on it.
          */}
          {e.source === 'derived' && (
            noting ? (
              <div className="viaform">
                <h3>This was somebody acting as {managerShort(e.actor)}</h3>
                <label className="viafield">
                  <span className="peyebrow">At the keyboard</span>
                  <select value={via} onChange={(ev) => setVia(ev.target.value)}>
                    <option value="">Nobody — it was {managerShort(e.actor)}</option>
                    {GMS.filter((g) => g.toUpperCase() !== e.actor).map((g) => (
                      <option key={g} value={g}>{managerShort(g)}</option>
                    ))}
                  </select>
                </label>
                <label className="viafield">
                  <span className="peyebrow">Why you know</span>
                  <input value={why} placeholder="optional"
                    onChange={(ev) => setWhy(ev.target.value)} />
                </label>
                <p className="fact">The record’s name stays on the row. A later note wins on read; both survive.</p>
                <div className="viaacts">
                  <button type="button" className="btn" onClick={() => setNoting(false)}>Cancel</button>
                  <button type="button" className="btn primary" onClick={() => onVia(via, why)}>
                    Save the note
                  </button>
                </div>
              </div>
            ) : (
              <button type="button" className="linkbtn" onClick={() => setNoting(true)}>
                {e.via ? 'Change who was really at the keyboard' : 'This was somebody acting as ' + managerShort(e.actor)}
              </button>
            )
          )}
        </div>
      </aside>
    </>
  );
}

function Fact(
  { label, value, mono, tone }:
  { label: string; value: string; mono?: boolean; tone?: 'amber' },
) {
  return (
    <>
      <dt>{label}</dt>
      <dd className={`${mono ? 'mono' : ''} ${tone ?? ''}`}>{value}</dd>
    </>
  );
}

function Row({ e, me, tz, onOpen }:
  { e: AuditEvent; me: string; tz: string; onOpen: () => void }) {
  return (
    <li className={`actrow ${e.kind} ${e.actor === me.toUpperCase() ? 'mine' : ''}`}>
      {/*
        THE WHOLE ROW IS THE CONTROL. A row with one small "details" link is a row whose useful
        target is 3% of its area, and this list is read on a phone.
      */}
      <button type="button" className="actopen" onClick={onOpen}
        aria-label={`${e.what} — open the detail`} />
      <time className="actat" dateTime={e.at}>{clock24In(e.at, tz)}</time>
      <span className={`actkind ${e.kind}`}>{KIND_LABEL[e.kind]}</span>
      <span className="actwho">
        <GmBadge gm={e.actor} withName />
        {/*
          WHO WAS REALLY AT THE KEYBOARD, beside the name the record carries rather than instead of
          it. The record says John and that is a fact; that Tim was acting as him is a second fact,
          and replacing the first with the second would be the log editing history.
        */}
        {e.via && (
          <span className={`actvia ${e.viaInferred ? 'inferred' : ''}`}
            title={e.viaInferred
              ? `${e.via} was acting as ${e.actor} at this moment — inferred, not recorded`
              : `${e.via} did it`}>
            via {managerShort(e.via)}
          </span>
        )}
      </span>
      <span className="actwhat">
        {e.what}
        {/*
          "on behalf of" is a fact about authority, not decoration: a commissioner editing Luke's
          slate and Luke editing it are different events, and a log that printed them the same
          would be the log getting a proxy wrong.
        */}
        {e.onBehalfOf && <span className="actfor">for {managerShort(e.onBehalfOf)}</span>}
      </span>
      <span className="acttarget">{e.target ?? ''}</span>
      {/* Which half it came from. A stored row is a browser's claim; a derived one is the record. */}
      <span className={`actsrc ${e.source}`}
        title={e.source === 'derived'
          ? 'Read off the record itself'
          : 'Written by a browser at the time'}>
        {e.source === 'derived' ? 'record' : 'logged'}
      </span>
    </li>
  );
}

const DAY = new Intl.DateTimeFormat('en-GB', {
  weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
});

function dayLabel(iso: string, tz: string): string {
  /* "Today" has to be today in the same zone the rows are grouped by, or the heading lies. */
  if (iso === dayIn(new Date().toISOString(), tz)) return 'Today';
  return DAY.format(new Date(`${iso}T12:00:00Z`));
}
